import { useEffect, useState } from 'react';
import { Search, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Transaction, formatCurrency, formatDate, CATEGORY_LABELS } from '@/lib/index';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';

const PAGE_SIZE = 20;

interface TransactionWithProfile extends Transaction {
  profile?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export default function AdminTransactions() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<TransactionWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);

  useEffect(() => {
    if (!user) return;
    fetchTransactions();
  }, [page, typeFilter, search, user]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('transactions')
        .select('*, profiles!transactions_user_id_fkey(first_name, last_name, email)', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter);
      }

      if (search.trim()) {
        query = query.or(`description.ilike.%${search}%,reference.ilike.%${search}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      const txWithProfiles = (data || []).map((tx: any) => ({
        ...tx,
        profile: tx.profiles,
      }));

      setTransactions(txWithProfiles);
      setTotalCount(count || 0);

      let creditQuery = supabase
        .from('transactions')
        .select('amount', { count: 'exact' })
        .in('type', ['credit', 'transfer_in', 'salary', 'deposit', 'interest']);

      let debitQuery = supabase
        .from('transactions')
        .select('amount', { count: 'exact' })
        .in('type', ['debit', 'transfer_out', 'payment', 'withdrawal', 'fee']);

      if (search.trim()) {
        creditQuery = creditQuery.or(`description.ilike.%${search}%,reference.ilike.%${search}%`);
        debitQuery = debitQuery.or(`description.ilike.%${search}%,reference.ilike.%${search}%`);
      }

      const [creditResult, debitResult] = await Promise.all([
        creditQuery,
        debitQuery,
      ]);

      const creditSum = (creditResult.data || []).reduce((sum, tx) => sum + (tx.amount || 0), 0);
      const debitSum = (debitResult.data || []).reduce((sum, tx) => sum + (tx.amount || 0), 0);

      setTotalCredit(creditSum);
      setTotalDebit(debitSum);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const TYPE_FILTERS = [
    { v: 'all', l: 'Wszystkie' },
    { v: 'credit', l: 'Wpłaty' },
    { v: 'debit', l: 'Wypłaty' },
    { v: 'transfer_in', l: 'Przelewy przychodzące' },
    { v: 'transfer_out', l: 'Przelewy wychodzące' },
    { v: 'payment', l: 'Płatności' },
    { v: 'salary', l: 'Wynagrodzenie' },
    { v: 'fee', l: 'Opłaty' },
    { v: 'interest', l: 'Odsetki' },
  ];

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const netBalance = totalCredit - totalDebit;

  return (
    <AdminLayout title="Transakcje">
      <div className="max-w-7xl mx-auto space-y-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border/50 bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success-light flex items-center justify-center">
                  <ArrowDownLeft className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Łączne wpłaty</p>
                  <p className="text-xl font-bold text-success">{formatCurrency(totalCredit)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50 bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-light flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-danger" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Łączne wypłaty</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(totalDebit)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50 bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center">
                  <div className="w-5 h-5 rounded-full border-2 border-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Saldo netto</p>
                  <p className={`text-xl font-bold ${netBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(netBalance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border/50 bg-card">
          <CardContent className="p-4 flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Szukaj po opisie, nazwisku klienta lub referencji..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className="pl-10 h-10 bg-background border-border"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.v}
                  onClick={() => {
                    setTypeFilter(f.v);
                    setPage(0);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    typeFilter === f.v
                      ? 'bg-accent/20 text-accent border border-accent/30'
                      : 'bg-muted/50 text-muted-foreground hover:text-foreground border border-transparent'
                  }`}
                >
                  {f.l}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Wszystkie transakcje</CardTitle>
              <span className="text-sm text-muted-foreground">
                Wyświetlanie {transactions.length} z {totalCount}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex gap-4 p-3 animate-pulse">
                    <div className="w-10 h-10 bg-muted rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-muted rounded w-1/2" />
                      <div className="h-2 bg-muted rounded w-1/3" />
                    </div>
                    <div className="w-20 h-8 bg-muted rounded" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Brak transakcji do wyświetlenia</p>
              </div>
            ) : (
              <div className="space-y-1">
                {transactions.map((tx, i) => {
                  const isPositive = ['credit', 'transfer_in', 'salary', 'deposit', 'interest'].includes(tx.type);
                  const clientName = tx.profile
                    ? `${tx.profile.first_name} ${tx.profile.last_name}`
                    : tx.user_id || 'Nieznany klient';

                  return (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted/20 transition-colors"
                    >
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          isPositive ? 'bg-success-light' : 'bg-danger-light'
                        }`}
                      >
                        {isPositive ? (
                          <ArrowDownLeft className="w-4 h-4 text-success" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-danger" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {tx.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">{clientName}</span>
                          {tx.reference && (
                            <>
                              <span className="mx-1">·</span>
                              <span>{tx.reference}</span>
                            </>
                          )}
                          <span className="mx-1">·</span>
                          <span>{CATEGORY_LABELS[tx.category || ''] || tx.category || 'Inne'}</span>
                          <span className="mx-1">·</span>
                          <span>{formatDate(tx.created_at)}</span>
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p
                          className={`text-sm font-bold ${
                            isPositive ? 'text-success' : 'text-foreground'
                          }`}
                        >
                          {isPositive ? '+' : '-'}
                          {formatCurrency(Math.abs(tx.amount), tx.currency)}
                        </p>
                        <Badge
                          variant={tx.status === 'completed' ? 'default' : 'secondary'}
                          className="text-xs mt-1"
                        >
                          {tx.status === 'completed'
                            ? 'Zakończona'
                            : tx.status === 'pending'
                            ? 'Oczekująca'
                            : tx.status === 'failed'
                            ? 'Nieudana'
                            : 'Anulowana'}
                        </Badge>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {!loading && transactions.length > 0 && (
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Poprzednia
                </Button>
                <span className="text-sm text-muted-foreground">
                  Strona {page + 1} z {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages - 1 || transactions.length < PAGE_SIZE}
                >
                  Następna
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}